import { useState, useEffect } from "react";
import "../../../styles/proMajazi/user/ProMajaziDashboard.css";
import ProMajaziTokens, { CustomSelect } from "./ProMajaziTokens";
import { fetchMyStores } from "../../../api/storeRequest";
import { getStoreData } from "../../../api/proMajazi";
import { swalError } from "../../../utils/swalAlert";
import moment from "jalali-moment";
import { convertToPersianDigits } from "../../../utils/stringUtil";

const STATUS = {
  paid: { label: "موفق", cls: "paid" },
  pending: { label: "در انتظار", cls: "pending" },
  rejected: { label: "ناموفق", cls: "rejected" },
  cancelled: { label: "لغو شده", cls: "cancelled" },
};

const USER_SORTS = [
  { value: "most_tokens", label: "بیشترین توکن" },
  { value: "least_tokens", label: "کمترین توکن" },
  { value: "newest", label: "جدیدترین استفاده" },
  { value: "oldest", label: "قدیمی‌ترین استفاده" },
];

const fa = (n) => Number(n).toLocaleString("fa-IR");

// تبدیل پاسخ API به ساختاری که کامپوننت استفاده می‌کند
// اگر نام فیلدهای بک‌اند فرق دارد، فقط همین‌جا را عوض کنید
const normalize = (d = {}) => ({
  balance: d.balance ?? 0,
  usedTokens: d.used_tokens ?? null,
  dailyLimit: d.daily_limit ?? null,
  tokenUnitPrice: Number(d.token_unit_price) || null,
  token_purchases: d.token_purchases || [],
  customer_usage: d.customer_usage || [],
});

export default function ProMajaziDashboard() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopsError, setShopsError] = useState(false);
  const [shopId, setShopId] = useState(null);

  // اطلاعات توکن فروشگاه انتخاب‌شده؛ شناسه‌ی فروشگاه کنار داده نگه داشته می‌شود
  // تا هنگام تعویض فروشگاه، داده‌ی فروشگاه قبلی اشتباهاً نمایش داده نشود
  const [tokenState, setTokenState] = useState({
    id: null,
    data: null,
    error: false,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [userSort, setUserSort] = useState("most_tokens");

  // دریافت لیست فروشگاه‌ها
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchMyStores();
        if (cancelled) return;
        const active = (res.data || []).filter((a) => a.status === "active");
        setShops(active);
        if (active.length) setShopId(active[0].store_id);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setShopsError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // دریافت اطلاعات توکن (شامل قیمت هر توکن) با هر بار تغییر فروشگاه (یا تلاش مجدد)
  useEffect(() => {
    if (!shopId) return;
    let cancelled = false; // جلوگیری از ریس‌کاندیشن هنگام تغییر سریع فروشگاه

    (async () => {
      try {
        const res = await getStoreData(shopId, { sort: userSort });
        if (cancelled) return;
        // اگر پاسخ لیست است (مثلاً res.data.results[0]) این خط را اصلاح کنید
        setTokenState({ id: shopId, data: normalize(res.data), error: false });
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setTokenState({ id: shopId, data: null, error: true });
        swalError({
          title: "خطا در دریافت اطلاعات توکن",
          text: err.response?.data?.detail || "دوباره تلاش کنید.",
          confirmButtonText: "باشه",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shopId, reloadKey, userSort]);

  const retryTokens = () => {
    setTokenState({ id: null, data: null, error: false });
    setReloadKey((k) => k + 1);
  };

  // رفرش بی‌صدا: داده‌ی فعلی نگه داشته می‌شود تا صفحه چشمک نزند
  const refreshTokens = () => setReloadKey((k) => k + 1);

  const current = tokenState.id === shopId ? tokenState : null;
  const tokenLoading = !current;
  const tokenError = !!current?.error;
  const tokenData = current?.data ?? null;

  // قیمت هر توکن (به دلار) از همان پاسخ getStoreData؛ تبدیل به ریال در ProMajaziTokens انجام می‌شود
  const tokenPrice = tokenData?.tokenUnitPrice ?? null;
  const priceError = !!tokenData && tokenPrice == null;

  const baseShop = shops.find((s) => s.store_id === shopId);
  const shop = baseShop && tokenData ? { ...baseShop, ...tokenData } : null;

  const updateDailyLimit = (value) =>
    setTokenState((prev) =>
      prev.id === shopId && prev.data
        ? { ...prev, data: { ...prev.data, dailyLimit: value } }
        : prev,
    );

  if (loading) return <div className="pmd">در حال بارگذاری...</div>;
  if (shopsError)
    return (
      <div className="pmd">
        خطا در دریافت فروشگاه‌ها. صفحه را دوباره بارگذاری کنید.
      </div>
    );
  if (!baseShop) return <div className="pmd">فروشگاهی یافت نشد</div>;

  const remaining = tokenData?.balance ?? 0;
  const hasUsed = tokenData?.usedTokens != null;
  const used = tokenData?.usedTokens ?? 0;
  const total = remaining + used;
  const usedPercent = total ? Math.round((used / total) * 100) : 0;
  const remainingPercent = total ? 100 - usedPercent : 0;
  const payment = tokenData?.token_purchases ?? [];
  const user = tokenData?.customer_usage ?? [];
  return (
    <div className="pmd">
      <div className="pmd-head">
        <h1>داشبورد پرومجازی</h1>
        <p>موجودی توکن، خرید و محدودیت، پرداخت‌ها و کاربران</p>
      </div>

      {/* انتخاب فروشگاه + خلاصه‌ی توکن همان فروشگاه */}
      <div className="pmd-card pmd-summary">
        <div className="pmd-sum-row">
          <div className="pmd-summary-shop">
            <label htmlFor="pmd-shop">فروشگاه</label>
            <CustomSelect
              id="pmd-shop"
              value={shopId}
              options={shops.map((x) => ({
                value: x.store_id,
                label: x.store_name,
              }))}
              onChange={setShopId}
            />
          </div>

          {tokenData && (
            <div className="pmd-sum-tokens">
              <div className={`pmd-gauge${hasUsed ? "" : " plain"}`}>
                {hasUsed && (
                  <svg
                    className="pmd-ring"
                    viewBox="0 0 120 120"
                    role="img"
                    aria-label={`${fa(remainingPercent)}٪ توکن باقی مانده`}
                  >
                    <circle className="pmd-ring-track" cx="60" cy="60" r="52" />
                    <circle
                      className="pmd-ring-fill"
                      cx="60"
                      cy="60"
                      r="52"
                      pathLength="100"
                      strokeDasharray="100"
                      strokeDashoffset={100 - remainingPercent}
                    />
                  </svg>
                )}
                <div className="pmd-gauge-center">
                  <strong>{fa(remaining)}</strong>
                  <span>توکن باقی‌مانده</span>
                </div>
              </div>

              {hasUsed && (
                <ul className="pmd-legend">
                  <li>
                    <i className="dot" />
                    باقی‌مانده <b>{fa(remaining)}</b>
                  </li>
                  <li>
                    <i className="dot used" />
                    مصرف‌شده <b>{fa(used)}</b>
                  </li>
                  <li className="pmd-legend-total">
                    از مجموع {fa(total)} توکن ({fa(usedPercent)}٪ مصرف)
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>

        {tokenLoading && (
          <div className="pmd-stat-note">در حال دریافت اطلاعات توکن...</div>
        )}

        {tokenError && (
          <div className="pmd-stat-note">
            دریافت اطلاعات توکن ناموفق بود.{" "}
            <button type="button" onClick={retryTokens}>
              تلاش مجدد
            </button>
          </div>
        )}
      </div>

      {/* خرید توکن و محدودیت همان فروشگاه */}
      {shop && (
        <ProMajaziTokens
          key={shop.store_id}
          shop={shop}
          tokenPrice={tokenPrice}
          priceError={priceError}
          onDailyLimitChange={updateDailyLimit}
          onPurchaseCreated={refreshTokens}
        />
      )}

      <div className="pmd-tables">
        {/* سابقه پرداخت */}
        <div className="pmd-card">
          <h2>آخرین پرداخت‌ها</h2>
          <div className="pmd-table-wrap pmd-scroll">
            <table className="pmd-table">
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>مبلغ (تومان)</th>
                  <th>توکن</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {payment.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="pmd-empty">
                      پرداختی ثبت نشده است
                    </td>
                  </tr>
                ) : (
                  payment.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {convertToPersianDigits(
                          moment(p.created_at).format("HH:mm - jYYYY/jMM/jDD"),
                        )}
                      </td>
                      <td>{fa(p.total_price)}</td>
                      <td>{fa(p.token_count)}</td>
                      <td>
                        <span className={`pmd-badge ${STATUS[p.status].cls}`}>
                          {STATUS[p.status].label}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* کاربران */}
        <div className="pmd-card">
          <div className="pmd-card-head">
            <h2>پرمصرف‌ترین کاربران</h2>
            <CustomSelect
              id="pmd-user-sort"
              value={userSort}
              options={USER_SORTS}
              onChange={setUserSort}
            />
          </div>
          <div className="pmd-table-wrap pmd-scroll">
            <table className="pmd-table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>شماره تماس</th>
                  <th>توکن مصرفی</th>
                  <th>آخرین استفاده</th>
                </tr>
              </thead>
              <tbody>
                {user.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="pmd-empty">
                      کاربری یافت نشد
                    </td>
                  </tr>
                ) : (
                  user.map((u) => (
                    <tr key={u.id}>
                      <td>{u.customer_name}</td>
                      <td>{convertToPersianDigits(u.customer_mobile)}</td>

                      <td>{fa(u.usage_count)}</td>
                      <td>
                        {convertToPersianDigits(
                          moment(u.usage_date).format("jYYYY/jMM/jDD"),
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
